import { inject, Injectable, NgZone } from '@angular/core';
import {
  BAUM_USED_FIELDS,
  BaumSelect,
  BaumSelectUsed,
  MangelBaumLevel,
  MangelBaumNode,
  MangelBaumNodeIds,
  MangelBaumNodeModel,
} from 'src/app/content/_components/_overlays/mangelbaum/mangelbaum.component';
import { DBStatus } from 'src/app/types/api';
import { AppDB } from './dexieDB';
import { AsMappedRow, AsMappedRows, GetDBStatus } from './sharedDB';
import { BaseItem } from 'src/app/content/_components/_modals/baseitems-wrapper/baseitems-wrapper.component';
import { BASE_ITEM_TABLE, BASE_ITEM_OTYPID_TABLE } from './baseitemsDB';
import { distinct, distinctBy, sortBy } from 'src/app/misc/functions';

//#region Interfaces for DB Structure
export interface SZUMZ extends DBStatus {
  ID?: number;
  OTYPID: number;
  GattungID: number;
  StammID: number;
  PruefartID: number;
  LageID: number;
  MangelID: number;
  InstandsetzungID: number;
  MengeneinheitID: number;
  BedAusloeserMin: string;
  BedMin: number;
  BedVerbindung: 'UND';
  BedAusloeserMax: string;
  BedMax: number;
  BedEinheit: string;
  FK: number;
  // Gesperrt?
  AAE: number;
  AW: number;
  KAV_ME?: string;
  EP: number;
  LVPos: string;
  zuAbt: string;
  S: number;
  V: number;
  D: number;
  Equipment: string;
  ZFeld1: string;
  ZFeld2: string;
  ZFeld3: string;
  ZFeld4: string;
  ZFeld5: string;
}
//#endregion

@Injectable({ providedIn: 'root' })
export class MangelBaumDB {
  private readonly db = inject(AppDB);
  private readonly zone = inject(NgZone);

  async LoadSelectWerten(OTYPID: number): Promise<BaumSelect> {
    const loader = new MangelBaumHelper(this.db, OTYPID);
    return await this.zone.runOutsideAngular(() => loader.LoadSelect());
  }

  async LoadTree(OTYPID: number, joinedIds: string): Promise<MangelBaumNode[]> {
    const loader = new MangelBaumHelper(this.db, OTYPID, joinedIds);
    return await this.zone.runOutsideAngular(() => loader.LoadTree());
  }

  async LoadEintrag(SZUMZID: number): Promise<MangelBaumNodeModel> {
    const loader = new MangelBaumHelper(this.db);
    return await loader.LoadEintrag(SZUMZID);
  }

  async SaveEintrag(OTYPID: number, model: MangelBaumNodeModel): Promise<{ id: number; exist: boolean }> {
    const loader = new MangelBaumHelper(this.db, OTYPID, model);
    return await loader.SaveEintrag(model);
  }

  async GetUsedUniqueSelection(): Promise<BaumSelectUsed> {
    return await this.zone.runOutsideAngular(async () => {
      const map = {} as BaumSelectUsed;

      await Promise.all(
        BAUM_USED_FIELDS.map(async field => {
          map[field] = (await this.db.szumz.orderBy(field).uniqueKeys()) as string[];
        })
      );

      return map;
    });
  }

  async GetMangelBaumAll(OTYPID: number): Promise<MangelBaumNodeModel[]> {
    const rows = await this.db.szumz.where({ OTYPID }).toArray();
    const mapped = AsMappedRows<MangelBaumNodeModel>()(rows, [
      'ID',
      'GattungID',
      'StammID',
      'PruefartID',
      'LageID',
      'MangelID',
      'InstandsetzungID',
      'FK',
      'MengeneinheitID',
      'BedAusloeserMin',
      'BedMin',
      ['BedVerbindung', 'AndBedingung'],
      'BedAusloeserMax',
      'BedMax',
      'BedEinheit',
      ['EP', 'Preis'],
      ['zuAbt', 'Abteilung'],
      'Equipment',
      'LVPos',
    ]);
    return mapped.map(row => {
      row.Bedingung = !!row.BedAusloeserMin;
      row.BedAusloeserMin ||= null;
      row.AndBedingung = row['AndBedingung' as string] == 'UND';
      row.BedAusloeserMax ||= null;
      row.BedEinheit ||= null;
      return row;
    });
  }
}

class MangelBaumHelper {
  private readonly db: AppDB;
  private readonly OTYPID: number;
  private readonly _ids: number[] = [];

  private get IsGleis() {
    return this.OTYPID == 2;
  }

  private readonly NextLevel: MangelBaumLevel;
  private readonly Filters: { OTYPID: number } & Partial<MangelBaumNodeIds>;

  constructor(db: AppDB, OTYPID?: number, joinedIds?: string | MangelBaumNodeModel) {
    this.db = db;
    this.OTYPID = OTYPID;
    if (joinedIds == undefined) return;

    let GattungID: number,
      StammID: number,
      PruefartID: number,
      LageID: number,
      MangelID: number,
      InstandsetzungID: number,
      ID: number;

    if (typeof joinedIds == 'string') {
      this._ids = joinedIds == '' ? [] : joinedIds.split(';').map(id => +id);
      if (this.IsGleis) {
        [GattungID, StammID, PruefartID, MangelID, InstandsetzungID] = this._ids;
        this.NextLevel = NEXT_LEVEL_GLEIS[this._ids.length];
      } else {
        [PruefartID, LageID, MangelID, InstandsetzungID] = this._ids;
        this.NextLevel = NEXT_LEVEL_NON_GLEIS[this._ids.length];
      }
    } else {
      ({ GattungID, StammID, PruefartID, LageID, MangelID, InstandsetzungID, ID } = joinedIds);
    }

    this.Filters = { OTYPID, GattungID, StammID, PruefartID, LageID, MangelID, InstandsetzungID, ID };
    for (const key in this.Filters) {
      if (this.Filters[key] === undefined) delete this.Filters[key];
    }
  }

  //#region Load Baum
  public async LoadTree(): Promise<MangelBaumNode[]> {
    const rows = await this.LoadNextLevel();
    return rows.map(row => ({
      ID: row.ID,
      Bezeichnung: row.Bezeichnung,
      Route: { level: this.NextLevel, ids: this._ids },
    }));
  }

  private LoadNextLevel(): Promise<BaseItem[]> {
    switch (this.NextLevel) {
      case MangelBaumLevel.Gattung:
        return this.LoadGattungen();
      case MangelBaumLevel.Stamm:
        return this.LoadStamm();
      case MangelBaumLevel.Prüfart:
        return this.LoadPrüfart();
      case MangelBaumLevel.Lage:
        return this.LoadLagen();
      case MangelBaumLevel.Mangel:
        return this.LoadMängel();
      case MangelBaumLevel.Instandsetzung:
        return this.LoadInstandsetzungen();
      case MangelBaumLevel.FK:
        return this.LoadFK();
      default:
        throw new Error(`MangelBaum.Level.${this.NextLevel}`);
    }
  }

  private async LoadGattungen(): Promise<BaseItem[]> {
    const ids = await this.FilterMangelBaum('GattungID');
    let rows = await this.db.sgatt.where({ OTYPID: this.OTYPID }).sortBy('IntID');
    rows = rows.filter(row => ids.includes(row.IntID));
    return AsMappedRows<BaseItem>()(rows, [['IntID', 'ID'], 'Bezeichnung']);
  }
  private async LoadStamm(): Promise<BaseItem[]> {
    return STAMM_STANDARD[this.Filters.GattungID] ?? (await this.LoadStammItems());
  }
  private async LoadStammItems(): Promise<BaseItem[]> {
    const ids = await this.FilterMangelBaum('StammID');
    return await this.LoadItems(GATTUNG_STAMM_TABLE[this.Filters.GattungID], ids);
  }
  private async LoadPrüfart(): Promise<BaseItem[]> {
    const ids = await this.FilterMangelBaum('PruefartID');
    return await this.LoadItems('sprue', ids);
  }
  private async LoadLagen(): Promise<BaseItem[]> {
    const ids = await this.FilterMangelBaum('LageID');
    return await this.LoadItems('slage', ids);
  }
  private async LoadMängel(): Promise<BaseItem[]> {
    const ids = await this.FilterMangelBaum('MangelID');
    return await this.LoadItems('smang', ids);
  }
  private async LoadInstandsetzungen(): Promise<BaseItem[]> {
    const ids = await this.FilterMangelBaum('InstandsetzungID');
    return await this.LoadItems('sinst', ids);
  }
  private async LoadFK(): Promise<BaseItem[]> {
    const rows = await this.FilterFK();
    const nodes: BaseItem[] = [];
    for (const row of rows) {
      const { ID, FK, BedAusloeserMin, BedMin, BedVerbindung, BedAusloeserMax, BedMax, BedEinheit } = row;
      const node: BaseItem = { ID, Bezeichnung: `${FK}` };

      if (BedAusloeserMin) {
        node.Bezeichnung += ' [';
        node.Bezeichnung += `${BedAusloeserMin} ${BedMin} ${BedEinheit}`;
        if (BedVerbindung && BedAusloeserMax) {
          node.Bezeichnung += ', ';
          node.Bezeichnung += `${BedAusloeserMax} ${BedMax} ${BedEinheit}`;
        }
        node.Bezeichnung += ']';
      }
      nodes.push(node);
    }

    return distinctBy(nodes, 'Bezeichnung');
  }

  private async LoadItems(table: MangelBaumTableDB, ids: number[]): Promise<BaseItem[]> {
    const rows = sortBy(await this.db[table].bulkGet(ids), 'Bezeichnung');
    return rows as BaseItem[];
  }

  private async FilterMangelBaum(joinId: keyof MangelBaumNodeIds): Promise<number[]> {
    const szumz = await this.db.szumz.where(this.Filters).toArray();
    return distinct(szumz, joinId);
  }

  private async FilterFK() {
    const rows = await this.db.szumz.where(this.Filters).sortBy('FK');
    return AsMappedRows<SZUMZ_FK>()(rows, [
      'ID',
      'FK',
      'BedAusloeserMin',
      'BedMin',
      'BedVerbindung',
      'BedAusloeserMax',
      'BedMax',
      'BedEinheit',
    ]);
  }
  //#endregion

  //#region Load/Save Eintrag
  public async LoadEintrag(SZUMZID: number): Promise<MangelBaumNodeModel> {
    const row = await this.db.szumz.get(SZUMZID);
    if (!row) return null;

    const node = AsMappedRow<MangelBaumNodeModel>()(row, [
      'ID',
      'GattungID',
      'StammID',
      'PruefartID',
      'LageID',
      'MangelID',
      'InstandsetzungID',
      'FK',
      'MengeneinheitID',
      'BedAusloeserMin',
      'BedMin',
      'BedAusloeserMax',
      'BedMax',
      'BedEinheit',
      ['EP', 'Preis'],
      ['zuAbt', 'Abteilung'],
      'Equipment',
      'LVPos',
      'ZFeld1',
      'ZFeld2',
      'ZFeld3',
      'ZFeld4',
      'ZFeld5',
    ]);
    node.Bedingung = !!row.BedAusloeserMin;
    node.AndBedingung = row.BedVerbindung == 'UND';
    return node;
  }

  public async SaveEintrag(model: MangelBaumNodeModel): Promise<{ id: number; exist: boolean }> {
    const szumz = AsMappedRow<SZUMZ>()(model, [
      'GattungID',
      'StammID',
      'PruefartID',
      'LageID',
      'MangelID',
      'InstandsetzungID',
      'FK',
      'MengeneinheitID',
      'BedAusloeserMin',
      'BedMin',
      'BedAusloeserMax',
      'BedMax',
      'BedEinheit',
      ['Preis', 'EP'],
      ['Abteilung', 'zuAbt'],
      'Equipment',
      'LVPos',
      'ZFeld1',
      'ZFeld2',
      'ZFeld3',
      'ZFeld4',
      'ZFeld5',
    ]);
    const id = await this.db.szumz.add({
      ...szumz,
      OTYPID: this.OTYPID,
      BedVerbindung: model.AndBedingung ? 'UND' : null,
      ...GetDBStatus(),
    });

    return { id, exist: false };
  }

  //#endregion

  //#region Select
  public async LoadSelect(): Promise<BaumSelect> {
    const gattungen: BaseItem[] = this.IsGleis ? await this.SelectGattungen() : [];
    return {
      Gattungen: gattungen,
      StammItems: this.IsGleis ? await this.SelectStammItems(gattungen.map(s => s.ID)) : {},
      Prüfarten: await this.SelectByOTYPID('sprue'),
      Lagen: !this.IsGleis ? await this.SelectByOTYPID('slage') : [],
      Mängel: await this.SelectByOTYPID('smang'),
      Instandsetzungen: await this.SelectByOTYPID('sinst'),
      Mengeneinheiten: await this.Select('smeh'),
      Equipment: await this.SelectByOTYPID('kequi'),
    };
  }

  private async SelectGattungen(): Promise<BaseItem[]> {
    const rows = await this.db.sgatt.where({ OTYPID: this.OTYPID }).sortBy('IntId');
    return AsMappedRows<BaseItem>()(rows, [['IntID', 'ID'], 'Bezeichnung']);
  }

  private async SelectStammItems(gattungIds: number[]): Promise<Record<number, BaseItem[]>> {
    const stammItems = { ...STAMM_STANDARD };
    for (const gattungId of gattungIds) {
      if (stammItems[gattungId]) continue;
      const table: StammTableDB = GATTUNG_STAMM_TABLE[gattungId];
      let rows: BaseItem[];
      if (table) {
        rows = await this.Select(table);
      } else {
        rows = await this.SelectByGattungId(gattungId);
      }
      stammItems[gattungId] = AsMappedRows<BaseItem>()(rows, ['ID', 'Bezeichnung']);
    }

    return stammItems;
  }

  private async SelectByGattungId(GattungID: number): Promise<BaseItem[]> {
    const rows = await this.db.sstamm.where({ OTYPID: this.OTYPID, GattungID }).sortBy('Bezeichnung');
    return AsMappedRows<BaseItem>()(rows, ['ID', 'Bezeichnung']);
  }

  private async SelectByOTYPID(table: BASE_ITEM_OTYPID_TABLE): Promise<BaseItem[]> {
    const rows = await this.db[table].where({ OTYPID: this.OTYPID }).sortBy('Bezeichnung');
    return AsMappedRows<BaseItem>()(rows, ['ID', 'Bezeichnung']);
  }

  private async Select(table: BASE_ITEM_TABLE): Promise<BaseItem[]> {
    const rows = await this.db[table].orderBy('Bezeichnung').toArray();
    return AsMappedRows<BaseItem>()(rows, ['ID', 'Bezeichnung']);
  }
  //#endregion
}

type StammTableDB = 'sbeft' | 'sschw' | 'seind' | 'sbauw';
type MangelBaumTableDB = 'sgatt' | 'sstamm' | StammTableDB | 'sprue' | 'slage' | 'smang' | 'sinst';

const STAMM_STANDARD = {
  1: [
    { ID: 1, Bezeichnung: 'Vignol' },
    { ID: 2, Bezeichnung: 'Rille' },
  ],
  4: [
    { ID: 1, Bezeichnung: 'Schotterbett' },
    { ID: 2, Bezeichnung: 'Unterbau' },
  ],
  6: [{ ID: 1, Bezeichnung: 'Lichtraumprofil' }],
  8: [
    { ID: 1, Bezeichnung: 'Schweißstöße' },
    { ID: 2, Bezeichnung: 'Laschenverbindung' },
    { ID: 3, Bezeichnung: 'ISO-Stoß' },
  ],
  9: [{ ID: 1, Bezeichnung: 'Grundinstandsetzung' }],
  10: [
    { ID: 1, Bezeichnung: 'Spurweite' },
    { ID: 2, Bezeichnung: 'Verwindung' },
    { ID: 3, Bezeichnung: 'Richtung' },
    { ID: 4, Bezeichnung: 'Neigung' },
    { ID: 5, Bezeichnung: 'Überhöhung' },
  ],
} as const satisfies Record<number, BaseItem[]>;

const GATTUNG_STAMM_TABLE = {
  2: 'sbeft',
  3: 'sschw',
  5: 'seind',
  7: 'sbauw',
} as const satisfies Record<number, StammTableDB>;

type SZUMZ_FK = Pick<
  SZUMZ,
  'ID' | 'FK' | 'BedAusloeserMin' | 'BedMin' | 'BedVerbindung' | 'BedAusloeserMax' | 'BedMax' | 'BedEinheit'
>;

export const NEXT_LEVEL_GLEIS = {
  0: MangelBaumLevel.Gattung,
  1: MangelBaumLevel.Stamm,
  2: MangelBaumLevel.Prüfart,
  3: MangelBaumLevel.Mangel,
  4: MangelBaumLevel.Instandsetzung,
  5: MangelBaumLevel.FK,
} as const satisfies Record<number, MangelBaumLevel>;

export const NEXT_LEVEL_NON_GLEIS = {
  0: MangelBaumLevel.Prüfart,
  1: MangelBaumLevel.Lage,
  2: MangelBaumLevel.Mangel,
  3: MangelBaumLevel.Instandsetzung,
  4: MangelBaumLevel.FK,
} as const satisfies Record<number, MangelBaumLevel>;
